/* ============================================================================
   Species catalogue. Agronomic values are the input for the recommendation
   service and the care schedule — the UI only displays them.
   ========================================================================== */

import type { Species } from '@core/types'

export const SPECIES: Species[] = [
  {
    id: 'sp-sidr',
    commonName: 'Sidr (Christ thorn jujube)',
    commonNameAr: 'سدر',
    scientificName: 'Ziziphus spina-christi',
    description:
      'Native thorny tree for the Iraqi plain. Deep roots and waxy leaves let it survive long dry spells, and it produces edible fruit in autumn.',
    difficulty: 'easy',
    waterNeed: 'low',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['sandy', 'loamy', 'chalky', 'saline'],
    matureHeightM: 7,
    yearsToMaturity: 5,
    nativeToIraq: true,
    careSummary:
      'Water deeply every 10–14 days for the first year, then rainfall and occasional deep watering are enough. Prune only dead wood.',
  },
  {
    id: 'sp-date-palm',
    commonName: 'Date palm',
    commonNameAr: 'نخلة التمر',
    scientificName: 'Phoenix dactylifera',
    description:
      'The national tree of Iraq and the backbone of southern orchards. Needs irrigation but tolerates salinity better than almost any fruit crop.',
    difficulty: 'moderate',
    waterNeed: 'high',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['loamy', 'sandy', 'saline'],
    matureHeightM: 20,
    yearsToMaturity: 7,
    nativeToIraq: true,
    careSummary:
      'Irrigate weekly in summer, less in winter. Remove suckers each spring and protect young palms from goats and borers.',
  },
  {
    id: 'sp-euphrates-poplar',
    commonName: 'Euphrates poplar',
    commonNameAr: 'الغرب',
    scientificName: 'Populus euphratica',
    description:
      'Fast-growing river-bank native. Establishes shade and wind shelter quickly, and tolerates seasonal flooding and moderate salinity.',
    difficulty: 'easy',
    waterNeed: 'high',
    sunExposure: 'full-sun',
    salinityTolerance: 'medium',
    soilTypes: ['loamy', 'clay', 'saline'],
    matureHeightM: 14,
    yearsToMaturity: 4,
    nativeToIraq: true,
    careSummary:
      'Keep the soil consistently moist for the first two summers. Expect a working life of 30–40 years rather than a century.',
  },
  {
    id: 'sp-athel',
    commonName: 'Athel tamarisk',
    commonNameAr: 'الأثل',
    scientificName: 'Tamarix aphylla',
    description:
      'The most salt-tolerant tree available in Iraq. Frequently planted as a field boundary or shelterbelt where nothing else survives.',
    difficulty: 'easy',
    waterNeed: 'low',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['sandy', 'saline', 'chalky'],
    matureHeightM: 10,
    yearsToMaturity: 4,
    nativeToIraq: true,
    careSummary:
      'Almost self-sufficient once established. Water fortnightly for the first six months, then stop.',
  },
  {
    id: 'sp-white-mulberry',
    commonName: 'White mulberry',
    commonNameAr: 'التوت الأبيض',
    scientificName: 'Morus alba',
    description:
      'Fast shade tree with edible fruit for families and birds. Widely naturalised along Iraqi canals and streets.',
    difficulty: 'easy',
    waterNeed: 'moderate',
    sunExposure: 'full-sun',
    salinityTolerance: 'medium',
    soilTypes: ['loamy', 'clay', 'chalky'],
    matureHeightM: 9,
    yearsToMaturity: 3,
    nativeToIraq: false,
    careSummary:
      'Water weekly in the first summer. Prune after fruiting to keep the canopy low enough to harvest.',
  },
  {
    id: 'sp-oleander',
    commonName: 'Oleander',
    commonNameAr: 'الدفلة',
    scientificName: 'Nerium oleander',
    description:
      'Evergreen flowering shrub for street and courtyard planting. Flowers through the hottest months with very little water.',
    difficulty: 'easy',
    waterNeed: 'low',
    sunExposure: 'full-sun',
    salinityTolerance: 'medium',
    soilTypes: ['sandy', 'loamy', 'chalky'],
    matureHeightM: 3,
    yearsToMaturity: 2,
    nativeToIraq: false,
    careSummary:
      'Water every 10 days in summer. Cut the oldest third of stems each winter to keep it flowering.',
  },
  {
    id: 'sp-bougainvillea',
    commonName: 'Bougainvillea',
    commonNameAr: 'الجهنمية',
    scientificName: 'Bougainvillea glabra',
    description:
      'Vigorous flowering climber that covers walls and pergolas with colour from spring to autumn, with minimal water once rooted.',
    difficulty: 'moderate',
    waterNeed: 'low',
    sunExposure: 'full-sun',
    salinityTolerance: 'medium',
    soilTypes: ['sandy', 'loamy', 'chalky'],
    matureHeightM: 5,
    yearsToMaturity: 2,
    nativeToIraq: false,
    careSummary:
      'Water deeply but infrequently, and let the soil dry between waterings to encourage flowering. Protect the root zone from frost.',
  },
  {
    id: 'sp-washingtonia',
    commonName: 'Washingtonia palm',
    commonNameAr: 'واشنطنيا',
    scientificName: 'Washingtonia filifera',
    description:
      'Tall, fast ornamental palm used for avenues and compound entrances. Handles heat, wind and saline irrigation water.',
    difficulty: 'easy',
    waterNeed: 'moderate',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['sandy', 'loamy', 'saline'],
    matureHeightM: 15,
    yearsToMaturity: 6,
    nativeToIraq: false,
    careSummary:
      'Irrigate every 7–10 days in summer for the first three years. Remove dried fronds yearly for fire safety.',
  },
  {
    id: 'sp-lemon',
    commonName: 'Lemon',
    commonNameAr: 'الليمون',
    scientificName: 'Citrus limon',
    description:
      'Productive courtyard fruit tree with high market value. Requires reliable irrigation and clean, low-salinity soil.',
    difficulty: 'demanding',
    waterNeed: 'high',
    sunExposure: 'partial-shade',
    salinityTolerance: 'low',
    soilTypes: ['loamy', 'chalky'],
    matureHeightM: 4,
    yearsToMaturity: 4,
    nativeToIraq: false,
    careSummary:
      'Water every 3–4 days in summer, never letting the root ball dry out. Feed with citrus fertiliser in February and June.',
  },
  {
    id: 'sp-eucalyptus',
    commonName: 'River red gum',
    commonNameAr: 'الكافور',
    scientificName: 'Eucalyptus camaldulensis',
    description:
      'Very fast shade and timber tree. Establishes quickly on degraded land but consumes large amounts of water.',
    difficulty: 'easy',
    waterNeed: 'moderate',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['clay', 'loamy', 'saline'],
    matureHeightM: 18,
    yearsToMaturity: 5,
    nativeToIraq: false,
    careSummary:
      'Water weekly for the first year only. Space trees at least 6 m apart so roots do not compete with crops.',
  },
  {
    id: 'sp-conocarpus',
    commonName: 'Conocarpus',
    commonNameAr: 'الكونوكاربس',
    scientificName: 'Conocarpus erectus',
    description:
      'Fast evergreen hedge plant widely used in Iraqi cities. Survives heat and saline water, and forms a dense screen in two seasons.',
    difficulty: 'easy',
    waterNeed: 'low',
    sunExposure: 'full-sun',
    salinityTolerance: 'high',
    soilTypes: ['sandy', 'saline', 'loamy'],
    matureHeightM: 6,
    yearsToMaturity: 2,
    nativeToIraq: false,
    careSummary:
      'Trim two or three times a year to keep the hedge dense. Requires no feeding once established.',
  },
]
